import type { RouteMatch } from './types';

function stripQuery(url: string): string {
  return url.split('?')[0] ?? url;
}

export interface RouteMatcher {
  isOnlineOnly(url: string): boolean;
  matchRoute(method: string, url: string): RouteMatch | null;
  getCacheKey(url: string, params?: Record<string, unknown>): string;
}

export function createRouteMatcher(onlineOnlyPatterns: RegExp[]): RouteMatcher {
  function isOnlineOnly(url: string): boolean {
    const path = stripQuery(url);
    return onlineOnlyPatterns.some((pattern) => pattern.test(path));
  }

  function matchRoute(method: string, url: string): RouteMatch | null {
    const path = stripQuery(url);
    const m = method.toUpperCase();

    if (isOnlineOnly(path)) {
      return { entity: 'clients', operation: 'get', onlineOnly: true };
    }

    if (path === '/auth/me') return null;

    if (path === '/trainers/me') {
      return { entity: 'trainerProfile', operation: m === 'GET' ? 'get' : 'update' };
    }

    if (path === '/analytics/retention') {
      return { entity: 'analyticsRetention', operation: 'get' };
    }

    if (path === '/analytics/funnel') {
      return { entity: 'analyticsFunnel', operation: 'get' };
    }

    if (path === '/clients') {
      return { entity: 'clients', operation: m === 'GET' ? 'list' : 'create' };
    }

    const clientMatch = path.match(/^\/clients\/([^/]+)$/);
    if (clientMatch) {
      return {
        entity: 'clients',
        operation: m === 'GET' ? 'get' : 'update',
        id: clientMatch[1],
        clientId: clientMatch[1],
      };
    }

    const clientCoinsMatch = path.match(/^\/clients\/([^/]+)\/coins$/);
    if (clientCoinsMatch) {
      return {
        entity: 'clientCoins',
        operation: m === 'GET' ? 'get' : 'update',
        clientId: clientCoinsMatch[1],
      };
    }

    const redeemMatch = path.match(/^\/clients\/([^/]+)\/coins\/redeem$/);
    if (redeemMatch) {
      return {
        entity: 'clientCoins',
        operation: 'action',
        clientId: redeemMatch[1],
      };
    }

    const packagesListMatch = path.match(/^\/clients\/([^/]+)\/packages$/);
    if (packagesListMatch) {
      return {
        entity: 'packages',
        operation: m === 'GET' ? 'list' : 'create',
        clientId: packagesListMatch[1],
      };
    }

    const packageItemMatch = path.match(
      /^\/clients\/([^/]+)\/packages\/([^/]+)(?:\/(?:use-session|restore-session))?$/,
    );
    if (packageItemMatch) {
      const isSessionAction =
        path.endsWith('/use-session') || path.endsWith('/restore-session');
      return {
        entity: 'packages',
        operation: isSessionAction ? 'action' : m === 'PATCH' ? 'update' : 'delete',
        clientId: packageItemMatch[1],
        id: packageItemMatch[2],
      };
    }

    if (path === '/bookings') {
      return { entity: 'bookings', operation: m === 'GET' ? 'list' : 'create' };
    }

    const bookingMatch = path.match(/^\/bookings\/([^/]+)$/);
    if (bookingMatch) {
      return {
        entity: 'bookings',
        operation: m === 'GET' ? 'get' : m === 'DELETE' ? 'delete' : 'update',
        id: bookingMatch[1],
      };
    }

    if (path === '/tasks') {
      return { entity: 'tasks', operation: m === 'GET' ? 'list' : 'create' };
    }

    const taskCompleteMatch = path.match(/^\/tasks\/([^/]+)\/complete$/);
    if (taskCompleteMatch) {
      return { entity: 'tasks', operation: 'action', id: taskCompleteMatch[1] };
    }

    const taskMatch = path.match(/^\/tasks\/([^/]+)$/);
    if (taskMatch) {
      return {
        entity: 'tasks',
        operation: m === 'DELETE' ? 'delete' : 'update',
        id: taskMatch[1],
      };
    }

    if (path === '/leads') {
      return { entity: 'leads', operation: m === 'GET' ? 'list' : 'create' };
    }

    const leadConvertMatch = path.match(/^\/leads\/([^/]+)\/convert$/);
    if (leadConvertMatch) {
      return {
        entity: 'leads',
        operation: 'action',
        id: leadConvertMatch[1],
        requiresNetwork: true,
      };
    }

    const leadMatch = path.match(/^\/leads\/([^/]+)$/);
    if (leadMatch) {
      return {
        entity: 'leads',
        operation: m === 'DELETE' ? 'delete' : 'update',
        id: leadMatch[1],
      };
    }

    if (path === '/challenges') {
      return { entity: 'challenges', operation: m === 'GET' ? 'list' : 'create' };
    }

    const challengeMatch = path.match(/^\/challenges\/([^/]+)$/);
    if (challengeMatch) {
      return {
        entity: 'challenges',
        operation: m === 'DELETE' ? 'delete' : 'update',
        id: challengeMatch[1],
      };
    }

    if (path === '/training-plans') {
      return { entity: 'trainingPlans', operation: m === 'GET' ? 'list' : 'create' };
    }

    const planMatch = path.match(/^\/training-plans\/([^/]+)$/);
    if (planMatch) {
      return {
        entity: 'trainingPlans',
        operation: m === 'DELETE' ? 'delete' : m === 'GET' ? 'get' : 'update',
        id: planMatch[1],
      };
    }

    const measurementsMatch = path.match(/^\/measurements\/([^/]+)$/);
    if (measurementsMatch) {
      const segment = measurementsMatch[1];
      if (m === 'POST') {
        return { entity: 'measurements', operation: 'create', clientId: segment };
      }
      if (m === 'GET') {
        return { entity: 'measurements', operation: 'list', clientId: segment };
      }
      if (m === 'PATCH') {
        return { entity: 'measurements', operation: 'update', id: segment };
      }
      if (m === 'DELETE') {
        return { entity: 'measurements', operation: 'delete', id: segment };
      }
    }

    return null;
  }

  function getCacheKey(url: string, params?: Record<string, unknown>): string {
    const search = params ? `?${JSON.stringify(params)}` : '';
    return `${url}${search}`;
  }

  return { isOnlineOnly, matchRoute, getCacheKey };
}
